import { useCallback } from 'react';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

/**
 * Captura coordenadas GPS con detalle del motivo de fallo.
 * Nunca rechaza: siempre resuelve con {lat, lng, ok, reason}.
 *
 * reason: null | 'unsupported' | 'denied' | 'unavailable' | 'timeout' | 'error'
 *
 * @returns {Promise<{lat: number|null, lng: number|null, ok: boolean, reason: string|null}>}
 */
export const captureGpsDetailed = () =>
  new Promise((resolve) => {
    if (!navigator?.geolocation) {
      return resolve({ lat: null, lng: null, ok: false, reason: 'unsupported' });
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, ok: true, reason: null }),
      (err) =>
        resolve({
          lat: null,
          lng: null,
          ok: false,
          reason:
            err?.code === 1
              ? 'denied'
              : err?.code === 2
              ? 'unavailable'
              : err?.code === 3
              ? 'timeout'
              : 'error',
        }),
      { timeout: 8000, maximumAge: 60000 }
    );
  });

/**
 * Captura coordenadas GPS del dispositivo de forma best-effort.
 * Nunca rechaza: si no hay permiso, señal o soporte → {lat: null, lng: null}.
 * Implementado sobre captureGpsDetailed — mismo contrato de siempre.
 *
 * @returns {Promise<{lat: number|null, lng: number|null}>}
 */
export const captureGps = () => captureGpsDetailed().then(({ lat, lng }) => ({ lat, lng }));

/**
 * Hook para registrar el inicio de visita en sitio sobre una entidad existente
 * (mantención u orden de trabajo). Captura GPS automáticamente antes de enviar.
 *
 * @returns {{ iniciarVisita: ({tipo: 'maintenance'|'order', id: number}) => Promise<object> }}
 */
export const useVisita = () => {
  const iniciarVisita = useCallback(async ({ tipo, id, lat, lng }) => {
    // Si el llamador ya capturó coordenadas (p. ej. el drawer, que mostró el
    // mapa), las reusamos y evitamos un segundo prompt de geolocalización.
    let coords = { lat, lng };
    if (lat == null && lng == null) {
      coords = await captureGps();
    }
    const token = getAccessTokenApi();
    const { data } = await axios.post(
      `${API_PATH}/visitas/iniciar`,
      { tipo, id, lat: coords.lat, lng: coords.lng },
      { headers: { Authorization: token } }
    );
    return data;
  }, []);

  return { iniciarVisita };
};
